import { createAction } from '@reduxjs/toolkit';
import { get, isEmpty } from 'lodash';
import { TABS } from 'state/modules/tabs';

export const ROUTE_AUTH = createAction<{
  query: {
    nextType: string;
    nextPayload: string | undefined;
  };
}>('route/ROUTE_AUTH');
export const ROUTE_HOME = createAction('route/ROUTE_HOME');
export const ROUTE_EXAMPLE_TAKEOVER = createAction(
  'route/ROUTE_EXAMPLE_TAKEOVER'
);
export const ROUTE_EXAMPLE_TABS = createAction<{
  tab: string;
}>('route/ROUTE_EXAMPLE_TABS');
export const ROUTE_FORBIDDEN = createAction('route/ROUTE_FORBIDDEN');
export const ROUTE_TABS = createAction<{
  tab: string;
  isClosePreview?: boolean;
  // VE-27161: the selected folder lives in the query string so it survives a
  // reload and can be bookmarked / shared.
  query?: RouteQuery;
}>('route/ROUTE_TABS');
export const ROUTE_FILE = createAction<{
  tab: string;
  tdoId: string | null;
  initFullScreen: boolean;
  query?: RouteQuery; // VE-27161
}>('route/ROUTE_FILE');
// export const refreshRoute = () => (dispatch, getState) => {
//   const currentLocation = getState().location;
//
//   dispatch({
//     type: currentLocation.type,
//     payload: currentLocation.payload,
//     meta: {
//       query: currentLocation.query
//     }
//   });
// };
type TabsKeys = keyof typeof TABS;
export interface Query {
  [key: string]: string | any;
}
export interface Payload {
  query?: Query | undefined;
  [key: string]: any;
  tab: (typeof TABS)[TabsKeys];
}
// VE-27161: query string carried by the tab/file routes.
export interface RouteQuery extends Query {
  folderId?: string;
}

// VE-27161: name of the query param holding the currently selected folder.
export const FOLDER_QUERY_PARAM = 'folderId';

export const selectCurrentRoutePayload = (state: any) =>
  state.location.payload as Payload;
export const selectRouteType = (state: any) => state.location.type;
export const selectRoutesMap = (state: any) => state.location.routesMap;
export const selectPreviousRoute = (state: any) => state.location.prev;
export const selectCurrentRouteReturnTo = (state: any) =>
  get(selectRoutesMap(state), [selectRouteType(state), 'returnTo']);

// VE-27161: redux-first-router only merges `query` into the URL when WE dispatch
// it (`action.payload.query` / `action.meta.query`); parsing an incoming URL
// puts it on `meta.query`, which the reducer stores as `location.query`. So the
// query string must always be read from here, never from the route payload.
export const selectCurrentRouteQuery = (state: any) =>
  (state.location.query ?? {}) as RouteQuery;
export const selectRouteFolderId = (state: any): string | undefined =>
  selectCurrentRouteQuery(state)[FOLDER_QUERY_PARAM];

// VE-27161: builds a route action that differs from the current location only
// by the folder query param -- `tab`, `tdoId`, `initFullScreen` and any other
// query params are carried over, so re-dispatching the route changes nothing
// else. A falsy `folderId` (root folder) drops the param, keeping the URL clean.
export const buildFolderRouteAction = (
  routeType: string,
  payload: Payload | undefined,
  query: RouteQuery | undefined,
  folderId: string | undefined
) => {
  const { [FOLDER_QUERY_PARAM]: _currentFolderId, ...otherQuery } = query ?? {};
  const nextQuery = folderId
    ? { ...otherQuery, [FOLDER_QUERY_PARAM]: folderId }
    : otherQuery;
  const { query: _payloadQuery, ...restPayload } = payload ?? {};

  return {
    type: routeType,
    payload: isEmpty(nextQuery)
      ? restPayload
      : { ...restPayload, query: nextQuery },
  };
};

export const navigateCurrentRouteReturnTo =
  () => (dispatch: any, getState: any) => {
    const action = get(selectCurrentRouteReturnTo(getState()), 'route');
    dispatch(action);
  };

export const navigateCurrentTab = () => (dispatch: any, getState: any) => {
  const state = getState();
  const tab = selectCurrentRoutePayload(state)?.tab ?? TABS.Analytics;
  // VE-27161: keep the selected folder in the URL when leaving a file route.
  const action = buildFolderRouteAction(
    ROUTE_TABS.type,
    { tab } as Payload,
    selectCurrentRouteQuery(state),
    selectRouteFolderId(state)
  );
  dispatch(action);
};
